import Box from '@mui/material/Box';
import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Inspection  } from "assets/SideBarIcons/inspection.svg";
import BackendPaginationTable from 'common/Table/TableWithPagination';
import Loader from 'layout/Loader';
import { useGetVehicleInspectionService } from 'subModule/src/services/vehicle/useVehicle';
import InspectionSearchHeader from './InspectionSearchHeader';
import { useNavigate } from 'react-router-dom';
interface SearchFormData {
    [key: string]: string | any;
}
interface InspectionProps {
    vehicleId?: number;
}
export default function InspectionListing(props: InspectionProps) {
    const navigate = useNavigate();
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(props?.vehicleId ? 5 : 20);
    const [loading, setLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<string[]>(['title']);
    const [search, setSearch] = useState<any>({});
    const [searchFormData, setSearchFormData] = useState<SearchFormData>({ title: '', ...(props?.vehicleId && { plateNumber: '' }), customerName: '', start: null, end: null, customerPhone: '' });
    const { data: tableData, isFetching, refetch } = useGetVehicleInspectionService({ 
        pageNumber: pageNo, size: pageSize, ...search,
        ...(props?.vehicleId && { vehicleId: props.vehicleId }), 
    });
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        }).finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, search]);
    useEffect(() => {
        if (loading) handleSubmitSearch();
    }, [searchFormData.start, searchFormData.end]);
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const clearSearch = () => {
        setFilter(['title']);
        setSearchFormData({ title: '', ...(props?.vehicleId && { plateNumber: '' }), customerName: '', start: null, end: null, customerPhone: '' });
        if (Object.keys(search).length) {
            setSearch({});
        }
    }
    const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
        const { target: { value }, } = event;
        setFilter(typeof value === 'string' ? value.split(',') : value,);
        setSearchFormData(prevFormData => {
            const selectedFilters = typeof value === 'string' ? value.split(',') : value;
            const updatedFormData = Object.fromEntries(Object.keys(prevFormData).map(key =>
                [key, value.includes(key) ? prevFormData[key] : '']
            ));
            if (selectedFilters.includes('dateInterval')) {
                updatedFormData.start = prevFormData.start;
                updatedFormData.end = prevFormData.end;
            }
            return updatedFormData;
        });
        if (!value.length && Object.keys(search).length) {
            clearSearch();
        }
    };
    const handleChangeSearch = (field: string, value: any) => {
        const val = value;
        if(field === 'dateInterval') {
            setSearchFormData((prevData) => ({
                ...prevData,
                start: value[0],
                end: value[1]
            }));
            if ((value[0] && value[1]) || ((value[0] === null) && (value[1] === null))) {
                setLoading(true);
            }
        } else {
            setSearchFormData((prevData) => ({
                ...prevData,
                [field]: val,
            }));
        }
    };
    const handleSubmitSearch = () => {
        const filteredSearch: Record<string, string> = {};
        const { start, end, ...rest } = searchFormData;
        if (start && end) {
            filteredSearch.start = start;
            filteredSearch.end = end;
        } else {
            setSearchFormData((prev) => ({
                ...prev,
                start: null,
                end: null,
            }));
        }
        Object.entries(rest).forEach(([key, value]) => {
            if (value) {
                filteredSearch[key] = value?.trim();
            }
        });
        setSearch(filteredSearch);
        setPageNo(1);
        setPageSize(pageSize);
    };
  const showRowView = (row: any) => navigate("/inspection/" + row.id);
    return (
        <Box>
            {props?.vehicleId ? null : <ComponentHeaderTwo
                showBtn={false}
                showSaveBtn={false}
                onSubmit={() => { }}
                heading={t('Inspections')}
                icon={<Inspection height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
            />}
            {(isFetching || loading) ? <Loader /> : <BackendPaginationTable
                columns={[
                    { id: 'inspectionFormTitle', label: 'title', numeric: false },
                    { id: 'vehiclePlateNo', label: 'plateNumber', numeric: false },
                    { id: 'customerName', label: 'customerName', numeric: false },
                    { id: 'customerPhone', label: 'customerPhone', numeric: false },
                    { id: 'technicians', label: 'Technician', numeric: false },
                    { id: 'createdAt', label: 'Date', numeric: false }
                ]}
                data={tableData?.pages[0]?.data?.content || []}
                pageNo={pageNo}
                pageSize={pageSize}
                tableData={tableData?.pages[0]?.data || {}}
                showCheckbox={false}
                showActions={true}
                enableSorting={true}
                showHeader={true}
                showSearch={true}
                showFilter={true}
                showCreateButton={true}
                actions={[]}
                handleChangePage={handleChangePage}
                handleChangeRows={handleChangeRows}
                loadingSearch={false}
                rowClick={true}
                showRowView={showRowView}
                headerComponent={
                  <Box className="table-header-search">
                    <InspectionSearchHeader
                        searchFormData={searchFormData}
                        handleChangeFilter={handleChangeFilter}
                        handleChangeSearch={handleChangeSearch}
                        handleSubmitSearch={handleSubmitSearch}
                        clearSearch={clearSearch}
                        vehicleId={props?.vehicleId || null}
                        {...{ filter, search }}
                    />
                  </Box>
                }
            />}
        </Box>
    )
}